import {CapsuleTabs} from 'antd-mobile';
import React, {useEffect, useRef} from 'react';
import {useHomeStore} from './store';
import {Provider} from './service';
import Casino from './components/casino';
import {useLatest} from 'ahooks';
import Lottery from './components/lottery';
import './index.css';
import {goTo} from '@/utils';
import {useRoute} from '@react-navigation/native';
import {Image} from '@/components-v2';
import {GoldAreaItem} from '@/pages/home/home.type';
import HomeScratch from '@/pages/home/v2/components/home-scratch';
import {currentBrand} from '@/brands';

const annaPrimaryColor =
  (currentBrand.theme.basicColor?.primary as string) || '#E91100';

const gameTypeActiveStyle = {
  borderTopColor: annaPrimaryColor,
  background:
    'linear-gradient(180deg, rgba(255, 219, 219, 1) 0%, rgba(255, 255, 255, 0) 100%)',
};

const gameTypeTextActiveStyle = {
  color: annaPrimaryColor,
};

const CasinoProviderTab = ({provider}: {provider: Provider}) => {
  return (
    <div className="inline-flex items-center gap-1 min-w-max">
      <img
        src={provider.logo}
        alt="provider logo"
        className="w-4 h-4 object-cover rounded flex-shrink-0"
      />
      <span className="transition-all duration-200 flex-shrink-0 whitespace-nowrap">
        {provider.name}
      </span>
    </div>
  );
};

const GameTypeTab = ({
  gameType,
  selected,
}: {
  gameType: GoldAreaItem;
  selected: boolean;
}) => {
  return (
    <div
      className="flex flex-col items-center px-2 transition-all border-t-2 border-transparent"
      style={selected ? gameTypeActiveStyle : undefined}>
      <Image
        src={gameType.bgImgUrl}
        alt={gameType.moduleName}
        className="w-12 h-12 object-cover rounded flex-shrink-0"
      />
      <span
        className="font-bold transition-all duration-200"
        style={selected ? gameTypeTextActiveStyle : undefined}>
        {gameType.moduleName}
      </span>
    </div>
  );
};

interface HomeProps {
  scrollViewRef?: React.RefObject<any>;
  scrollY?: number;
}

export default function Home({scrollViewRef, scrollY}: HomeProps) {
  const providerTabsRef = useRef<HTMLDivElement>(null);
  const {
    casinoProviders,
    getProviders,
    handleProviderChange,
    selectedProvider,
    selectedGameType,
    handleGameTypeChange,
    getGameTypesList,
    gameTypesList,
  } = useHomeStore();

  const selectedGameTypeRef = useLatest(selectedGameType);
  const {params = {}} = useRoute<any>();

  useEffect(() => {
    if (selectedGameType === 'CASINO' || selectedGameType === 'LIVE') {
      const type = selectedGameType === 'CASINO' ? 'casino' : 'liveCasino';
      getProviders(type);
    }
  }, [getProviders, selectedGameType]);

  useEffect(() => {
    if (
      params.detailType &&
      selectedGameTypeRef.current !== params.detailType
    ) {
      handleGameTypeChange(params.detailType);
    }
  }, [params, handleGameTypeChange, selectedGameTypeRef]);

  const onProviderChange = (key: string) => {
    handleProviderChange(key);
  };

  const onGameTypeChange = (key: string) => {
    if (key === 'SPORTS') {
      goTo('Sports');
      return;
    }
    handleGameTypeChange(key);
  };

  useEffect(() => {
    getGameTypesList();
  }, [getGameTypesList]);

  return (
    <div className="pb-3 home-casino-container">
      <div className="game-type-tabs">
        <CapsuleTabs activeKey={selectedGameType} onChange={onGameTypeChange}>
          {gameTypesList.map(item => (
            <CapsuleTabs.Tab
              title={
                <GameTypeTab
                  gameType={item}
                  selected={selectedGameType === item.moduleType}
                />
              }
              key={item.moduleType}
            />
          ))}
        </CapsuleTabs>
      </div>

      <div>
        {(selectedGameType === 'CASINO' || selectedGameType === 'LIVE') && (
          <>
            <div ref={providerTabsRef} className="casino-provider-tabs py-3">
              <CapsuleTabs
                activeKey={selectedProvider}
                onChange={onProviderChange}>
                {casinoProviders.map(provider => (
                  <CapsuleTabs.Tab
                    title={<CasinoProviderTab provider={provider} />}
                    key={provider.id.toString()}
                  />
                ))}
              </CapsuleTabs>
            </div>
            <Casino />
          </>
        )}
        {selectedGameType === 'LOTTERY' && (
          <Lottery scrollViewRef={scrollViewRef} scrollY={scrollY} />
        )}
        {selectedGameType === 'SCRATCH' && <HomeScratch />}
      </div>
    </div>
  );
}
